import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  TextField,
  Button,
  Divider,
  Paper,
  Tabs,
  Tab,
  Grid,
  CircularProgress
} from '@mui/material';
import { AdPlacementConfig, InlineConfig } from '../services/publisherConfigService';

interface AdPlacementPreviewProps {
  config: AdPlacementConfig;
}

const SAMPLE_RESPONSES: Record<string, string> = {
  'text': `Creating a successful exercise routine starts with setting realistic goals. First, determine what you want to achieve - weight loss, muscle gain, or improved cardio fitness.

Next, schedule your workouts. Consistency is key, so choose times that fit your lifestyle. Starting with 3-4 sessions per week is reasonable for most beginners.

For beginners, try a mix of cardio and strength training. Cardio activities like walking, jogging, or cycling improve heart health and burn calories. Strength training with bodyweight exercises or light weights helps build muscle.

Remember to include warm-up and cool-down periods with each workout. This reduces injury risk and improves flexibility.

Track your progress but be patient with results. Fitness improvements take time, typically 4-8 weeks before you notice significant changes.

Finally, listen to your body. Some muscle soreness is normal, but pain is a warning sign. Rest when needed and adjust your routine as you progress.`,

  'markdown': `# Guide to Modern Web Development

## Getting Started
Web development today combines various technologies to create interactive applications. Here's how to begin:

1. **Learn the fundamentals**
   - HTML for structure
   - CSS for styling
   - JavaScript for interactivity

2. **Choose your path**
   - Frontend: React, Vue, or Angular
   - Backend: Node.js, Python/Django, or Ruby on Rails
   - Full-stack: Combining both approaches

## Essential Tools
Every web developer should be familiar with:

* **Version control**: Git and GitHub
* **Package managers**: npm or yarn
* **Build tools**: Webpack, Vite, or Parcel

## Best Practices
- Write clean, maintainable code
- Test thoroughly
- Optimize for performance
- Ensure accessibility
- Follow security guidelines

> "The best way to learn is by building projects that interest you."

## Learning Resources
Check out these resources to deepen your knowledge:
- MDN Web Docs
- freeCodeCamp
- CSS-Tricks`,

  'html': `<div class="article">
  <h1>The Benefits of Meditation</h1>
  
  <p class="intro">Meditation has been practiced for thousands of years and has numerous benefits for both mental and physical health.</p>
  
  <div class="section">
    <h2>Mental Benefits</h2>
    <ul>
      <li>Reduces stress and anxiety</li>
      <li>Improves focus and concentration</li>
      <li>Enhances self-awareness</li>
      <li>Promotes emotional health</li>
    </ul>
  </div>
  
  <div class="section">
    <h2>Physical Benefits</h2>
    <ul>
      <li>Lowers blood pressure</li>
      <li>Improves sleep quality</li>
      <li>Helps manage pain</li>
      <li>Strengthens immune system</li>
    </ul>
  </div>
  
  <div class="getting-started">
    <h2>Getting Started</h2>
    <p>Begin with just 5 minutes a day of mindful breathing. Find a quiet place, sit comfortably, and focus on your breath going in and out.</p>
    <p>As you become more comfortable, gradually increase your meditation time.</p>
  </div>

  <div class="conclusion">
    <p>Consistent practice is key to experiencing the full benefits of meditation. Even a few minutes daily can make a significant difference in your overall wellbeing.</p>
  </div>
</div>`
};

const SAMPLE_AD = {
  title: "Premium Fitness App",
  description: "Track workouts, get personalized plans, and reach your fitness goals faster.",
  cta: "Try Free for 30 Days",
  url: "https://example.com/fitness-app"
};

const MARKDOWN_AD = `
---
### [Premium Fitness App](#) - *Sponsored*
Track workouts, get personalized plans, and reach your fitness goals faster.
[Try Free for 30 Days](#)
---
`;

const HTML_AD = `
<div class="ad-container" style="border: 1px solid #e0e0e0; border-radius: 8px; padding: 12px; margin: 16px 0; background-color: #f9f9f9;">
  <div style="font-size: 12px; color: #757575; margin-bottom: 4px;">Sponsored</div>
  <h3 style="margin: 0 0 8px 0; color: #1a73e8;"><a href="#" style="text-decoration: none;">Premium Fitness App</a></h3>
  <p style="margin: 0 0 8px 0;">Track workouts, get personalized plans, and reach your fitness goals faster.</p>
  <a href="#" style="display: inline-block; background-color: #1a73e8; color: white; padding: 6px 12px; border-radius: 4px; text-decoration: none; font-weight: 500;">Try Free for 30 Days</a>
</div>
`;

const TEXT_AD = `
[Advertisement] Premium Fitness App: Track workouts, get personalized plans, and reach your fitness goals faster. Try Free for 30 Days: https://example.com/fitness-app
`;

// Ensure we have a complete inline config for preview purposes
const ensureCompleteInlineConfig = (config: AdPlacementConfig): AdPlacementConfig => {
  if (config.mode !== 'inline' || !config.inline_config) {
    return config;
  }
  
  return {
    ...config,
    inline_config: {
      tone_matching: true,
      skip_on_factual: true,
      insertion_density: 0.3,
      preferred_positions: ['paragraph_end', 'topic_transition'],
      blending_style: 'subtle',
      ...config.inline_config
    }
  };
};

const AdPlacementPreview: React.FC<AdPlacementPreviewProps> = ({ config }) => {
  // Ensure we have a complete config for preview
  const completeConfig = ensureCompleteInlineConfig(config);
  
  const [formatTab, setFormatTab] = useState<string>(completeConfig.response_format === 'auto' ? 'text' : (completeConfig.response_format || 'text'));
  const [customContent, setCustomContent] = useState<string>('');
  const [showCustomContent, setShowCustomContent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [previewGenerated, setPreviewGenerated] = useState(false);
  
  // Auto-generate preview when component mounts or config changes
  useEffect(() => {
    simulateAdPlacement();
  }, [completeConfig, formatTab]);
  
  const handleFormatChange = (event: React.SyntheticEvent, newFormat: string) => {
    setFormatTab(newFormat);
  };
  
  const getAdForFormat = (format: string) => {
    switch (format) {
      case 'markdown':
        return MARKDOWN_AD;
      case 'html':
        return HTML_AD;
      default:
        return TEXT_AD;
    }
  };
  
  const getContentWithAdPlacement = () => {
    const content = showCustomContent && customContent 
      ? customContent 
      : SAMPLE_RESPONSES[formatTab];
      
    if (!content) return '';
    
    const ad = getAdForFormat(formatTab);
    
    switch (completeConfig.mode) {
      case 'before':
        return `${ad}\n\n${content}`;
      case 'after':
        return `${content}\n\n${ad}`;
      case 'inline':
        // Simplified inline placement for preview
        if (formatTab === 'html') {
          const parts = content.split('</div>');
          if (parts.length > 2) {
            const insertPosition = Math.floor(parts.length / 2);
            return parts.slice(0, insertPosition).join('</div>') + 
                   '</div>' + 
                   ad + 
                   parts.slice(insertPosition).join('</div>');
          }
        } else {
          const paragraphs = content.split('\n\n');
          if (paragraphs.length > 1) {
            const insertPosition = Math.floor(paragraphs.length / 2);
            return [...paragraphs.slice(0, insertPosition), ad, ...paragraphs.slice(insertPosition)].join('\n\n');
          }
        }
        return content + '\n\n' + ad; // Fallback if we can't split content
      default:
        return content;
    }
  };
  
  const simulateAdPlacement = () => {
    setLoading(true);
    // Simulate API call delay
    setTimeout(() => {
      setLoading(false);
      setPreviewGenerated(true);
    }, 1000);
  };
  
  // Create a summary of the current configuration
  const getConfigSummary = () => {
    const parts = [
      `Mode: ${completeConfig.mode.charAt(0).toUpperCase() + completeConfig.mode.slice(1)}`,
      `Format: ${completeConfig.response_format || 'auto'}`
    ];
    
    if (completeConfig.mode === 'inline' && completeConfig.inline_config) {
      parts.push(`Style: ${completeConfig.inline_config.blending_style || 'subtle'}`);
      if (completeConfig.inline_config.tone_matching) {
        parts.push('Tone matching: Enabled');
      }
    }
    
    return parts.join(' • ');
  };
  
  return (
    <Card variant="outlined" sx={{ mb: 4 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Ad Placement Preview
        </Typography>
        
        <Box sx={{ 
          p: 2, 
          mb: 3, 
          bgcolor: '#f0f7ff', 
          borderRadius: 1,
          border: '1px solid #cce5ff'
        }}>
          <Typography variant="subtitle2" gutterBottom>
            Current Configuration Preview
          </Typography>
          <Typography variant="body2">
            {getConfigSummary()}
          </Typography>
        </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          See how your ads will appear with the current placement configuration
        </Typography>
        
        <Tabs
          value={formatTab}
          onChange={handleFormatChange}
          sx={{ mb: 2 }}
        >
          <Tab label="Text" value="text" />
          <Tab label="Markdown" value="markdown" />
          <Tab label="HTML" value="html" />
        </Tabs>
        
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2">
                Sample Response
              </Typography>
              <Button 
                size="small"
                sx={{ ml: 2 }}
                onClick={() => setShowCustomContent(!showCustomContent)}
              >
                {showCustomContent ? 'Use Sample' : 'Use Custom'}
              </Button>
            </Box>
            
            {showCustomContent ? (
              <TextField
                fullWidth
                multiline
                rows={6}
                value={customContent}
                onChange={(e) => setCustomContent(e.target.value)}
                placeholder={`Enter custom ${formatTab} content to see how ads would be placed...`}
                variant="outlined"
                sx={{ mb: 2 }}
              />
            ) : null}
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button 
                variant="contained"
                onClick={simulateAdPlacement}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : null}
              >
                {loading ? 'Generating...' : 'Generate Preview'}
              </Button>
            </Box>
          </Grid>
        </Grid>
        
        <Divider sx={{ mb: 3 }} />
        
        <Typography variant="subtitle2" gutterBottom>
          Preview with {completeConfig.mode === 'before' ? 'Prepended' : completeConfig.mode === 'after' ? 'Appended' : 'Inline'} Ad
        </Typography>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ 
            p: 2, 
            bgcolor: '#f5f5f5', 
            borderRadius: 1,
            border: '1px solid #e0e0e0',
            maxHeight: '500px',
            overflow: 'auto'
          }}>
            {formatTab === 'html' ? (
              <Box 
                dangerouslySetInnerHTML={{ __html: getContentWithAdPlacement() }} 
                sx={{ 
                  '& .ad-container': {
                    border: '2px solid #4caf50 !important',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }
                }}
              />
            ) : formatTab === 'markdown' ? (
              <Box 
                sx={{ 
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  '& h1, & h2, & h3': { fontFamily: 'inherit' },
                  '& hr': { borderColor: '#4caf50', borderWidth: '2px', my: 2 }
                }}
              >
                {getContentWithAdPlacement()}
              </Box>
            ) : (
              <Box 
                sx={{ 
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  '& [Advertisement]': { color: '#4caf50', fontWeight: 'bold' }
                }}
              >
                {getContentWithAdPlacement()}
              </Box>
            )}
          </Box>
        )}
        
        <Box sx={{ mt: 3 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Current Configuration:</strong> {completeConfig.mode} placement
            {completeConfig.mode === 'inline' && completeConfig.inline_config ? ` with ${completeConfig.inline_config.blending_style} blending` : ''}
            {completeConfig.response_format ? `, optimized for ${completeConfig.response_format === 'auto' ? 'auto-detected' : completeConfig.response_format} format` : ''}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default AdPlacementPreview; 